import React, { useState, useEffect, useCallback } from 'react';
import { Flag, ExternalLink } from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  open: 'bg-amber-50 text-amber-700',
  investigating: 'bg-sky-50 text-sky-700',
  resolved: 'bg-emerald-50 text-emerald-700',
  dismissed: 'bg-gray-100 text-gray-500',
};

const STATUS_FILTERS = [
  ['all', 'All'],
  ['open', 'Open'],
  ['investigating', 'Investigating'],
  ['resolved', 'Resolved'],
  ['dismissed', 'Dismissed'],
];

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
    <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value ?? '—'}</p>
  </div>
);

const Evidence = ({ report }) => {
  const urls = report.evidence_urls || [];
  const links = report.evidence_links || [];
  const notes = report.evidence_notes;
  if (urls.length === 0 && links.length === 0 && !notes) return null;
  return (
    <div className="basis-full mt-2 space-y-1.5">
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {urls.map((u, i) => (
            <a key={`u-${i}`} href={u} target="_blank" rel="noopener noreferrer"
               className="block w-12 h-12 rounded-md overflow-hidden border border-gray-200 hover:ring-2 hover:ring-violet-300">
              <img src={u} alt={`evidence ${i + 1}`} className="w-full h-full object-cover" />
            </a>
          ))}
        </div>
      )}
      {links.length > 0 && (
        <div className="flex flex-col gap-0.5">
          {links.map((l, i) => (
            <a key={`l-${i}`} href={l} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 truncate max-w-md">
              <ExternalLink className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{l}</span>
            </a>
          ))}
        </div>
      )}
      {notes && <p className="text-xs text-gray-500 italic">{notes}</p>}
    </div>
  );
};

const AdminReports = () => {
  const [tab, setTab] = useState('creator'); // creator | template
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const base = tab === 'creator' ? 'creator-reports' : 'template-reports';

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    const token = localStorage.getItem('admin_token');
    const h = { Authorization: `Bearer ${token}` };
    const listUrl = `${API_URL}/api/admin/marketplace/${base}` +
      (statusFilter !== 'all' ? `?status=${statusFilter}` : '');
    try {
      const [s, l] = await Promise.all([
        fetch(`${API_URL}/api/admin/marketplace/${base}/stats`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(listUrl, { headers: h }).then(r => r.ok ? r.json() : { items: [] }),
      ]);
      setStats(s);
      setItems(l.items || []);
    } finally {
      setLoading(false);
    }
  }, [base, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    setBusyId(id);
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(`${API_URL}/api/admin/marketplace/${base}/${id}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d.detail || 'Action failed'); }
      else await load();
    } finally { setBusyId(null); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === items.length && items.length > 0) return new Set();
      return new Set(items.map(r => r.id));
    });
  };

  const bulkAction = async (status) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const token = localStorage.getItem('admin_token');
    try {
      const r = await fetch(`${API_URL}/api/admin/marketplace/${base}/bulk`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: [...selectedIds],
          status,
          resolution: status === 'resolved' ? 'upheld' : 'rejected',
        }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d.detail || 'Bulk action failed'); }
      else { setSelectedIds(new Set()); await load(); }
    } finally { setBulkBusy(false); }
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return (
    <AdminLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Flag className="w-5 h-5 text-red-500" /> Reports
      </h1>

      {/* Creator / Template tabs */}
      <div className="flex gap-2 mb-6">
        {[['creator', 'Creator Reports'], ['template', 'Template Reports']].map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setStatusFilter('all'); }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total} />
        <StatCard label="Open" value={stats?.open} />
        <StatCard label="Investigating" value={stats?.investigating} />
        <StatCard label="Resolved" value={stats?.resolved} />
        <StatCard label="Dismissed" value={stats?.dismissed} />
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setStatusFilter(k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${statusFilter === k ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Bulk toolbar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-violet-50 border border-violet-100 rounded-lg">
          <span className="text-sm font-medium text-violet-800">{selectedIds.size} selected</span>
          <button disabled={bulkBusy} onClick={() => bulkAction('resolved')}
                  className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            Resolve selected
          </button>
          <button disabled={bulkBusy} onClick={() => bulkAction('dismissed')}
                  className="px-3 py-1.5 text-xs rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50">
            Dismiss selected
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 text-sm text-gray-500">No reports</div>
        ) : (
          <>
            {/* Select-all header */}
            <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50/60 flex items-center gap-3">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                     className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
              <span className="text-xs font-medium text-gray-500">Select all on page</span>
            </div>
            <div className="divide-y divide-gray-50">
              {items.map(rep => (
                <div key={rep.id} className="px-5 py-4 flex items-center gap-4 flex-wrap">
                  <input type="checkbox" checked={selectedIds.has(rep.id)} onChange={() => toggleSelect(rep.id)}
                         className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 capitalize">{rep.reason?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-400 truncate">{rep.description || 'No details'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {tab === 'creator' ? `Creator: ${rep.creator_id}` : `Template: ${rep.template_id}`} · {rep.created_at ? new Date(rep.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[rep.status] || ''}`}>{rep.status}</span>
                  {rep.status !== 'resolved' && rep.status !== 'dismissed' && (
                    <div className="flex gap-1.5">
                      <button disabled={busyId === rep.id} onClick={() => patch(rep.id, { status: 'investigating' })} className="px-2.5 py-1 text-xs rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100">Investigate</button>
                      <button disabled={busyId === rep.id} onClick={() => patch(rep.id, { status: 'resolved', resolution: 'upheld', ...(tab === 'creator' ? { suspend_creator: window.confirm('Also suspend this creator?') } : { unlist_template: window.confirm('Also unlist this template?') }) })} className="px-2.5 py-1 text-xs rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Resolve</button>
                      <button disabled={busyId === rep.id} onClick={() => patch(rep.id, { status: 'dismissed' })} className="px-2.5 py-1 text-xs rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200">Dismiss</button>
                    </div>
                  )}
                  <Evidence report={rep} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
